import React, { useMemo, useState } from "react";
import { useClients } from "@/hooks/useClients";
import { useFunds } from "@/hooks/useFunds";
import { useInvestments, useUpsertInvestment } from "@/hooks/useInvestments";
import { useMe } from "@/hooks/useMe";
import { PageHeader } from "@/components/PageHeader";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Search } from "lucide-react";

const fmtBRL0 = (v: number) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL", minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v);
const fmtUSD0 = (v: number) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "USD", minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v);
import { toast } from "sonner";

const ORIGIN_ORDER = ["Onshore", "Offshore", "Prospect On", "Prospect Off"] as const;

export default function Investments() {
  const { data: clients } = useClients();
  const { data: funds } = useFunds();
  const { data: investments } = useInvestments();
  const { data: me } = useMe();
  const isAdmin = me?.user.isAdmin ?? false;
  const upsert = useUpsertInvestment();
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState<{
    clientId: string;
    fundId: string;
    field: "brl" | "usd";
  } | null>(null);
  const [editValue, setEditValue] = useState("");

  const matrix = useMemo(() => {
    const m = new Map<string, Map<string, { brl: number; usd: number }>>();
    (investments ?? []).forEach((i) => {
      if (!m.has(i.client_id)) m.set(i.client_id, new Map());
      m.get(i.client_id)!.set(i.fund_id, {
        brl: Number(i.amount_brl),
        usd: Number(i.amount_usd),
      });
    });
    return m;
  }, [investments]);

  const grouped = useMemo(() => {
    const filtered = (clients ?? []).filter(
      (c) => !search || c.name.toLowerCase().includes(search.toLowerCase())
    );
    const map = new Map<string, typeof filtered>(ORIGIN_ORDER.map((o) => [o, []]));
    filtered.forEach((c) => {
      const bucket = map.get(c.origin) ?? [];
      bucket.push(c);
      map.set(c.origin, bucket);
    });
    return ORIGIN_ORDER.map((origin) => ({
      origin,
      clients: (map.get(origin) ?? []).sort((a, b) => a.name.localeCompare(b.name)),
    })).filter((g) => g.clients.length > 0);
  }, [clients, search]);

  const cellValue = (clientId: string, fundId: string, field: "brl" | "usd") =>
    matrix.get(clientId)?.get(fundId)?.[field] ?? 0;

  const startEdit = (clientId: string, fundId: string, field: "brl" | "usd") => {
    if (!isAdmin) return; // somente admin edita commitments
    setEditing({ clientId, fundId, field });
    setEditValue(String(cellValue(clientId, fundId, field) || ""));
  };

  const commit = async () => {
    if (!editing) return;
    const num = parseFloat(editValue) || 0;
    const existing = matrix.get(editing.clientId)?.get(editing.fundId) ?? { brl: 0, usd: 0 };
    try {
      await upsert.mutateAsync({
        client_id: editing.clientId,
        fund_id: editing.fundId,
        amount_brl: editing.field === "brl" ? num : existing.brl,
        amount_usd: editing.field === "usd" ? num : existing.usd,
      });
    } catch {
      toast.error("Error saving");
    }
    setEditing(null);
  };

  const fundList = funds ?? [];

  return (
    <div className="min-h-screen bg-background">
      <PageHeader title="IR Jus Capital" subtitle="Allocations by Fund" />
      <div className="container mx-auto px-4 py-8">
        <div className="relative mb-6 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search client..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>

        <div className="rounded-lg border border-border bg-card overflow-x-auto">
          <Table>
            <TableHeader>
              {/* Row 1: fund names spanning BRL + USD */}
              <TableRow style={{ backgroundColor: "rgb(212, 163, 123)" }} className="hover:opacity-90">
                <TableHead rowSpan={2} className="align-middle text-center border-r" style={{ color: "#fff", fontWeight: 600, backgroundColor: "rgb(212, 163, 123)", width: "170px", maxWidth: "170px" }}>
                  INVESTOR
                </TableHead>
                {fundList.map((f) => (
                  <TableHead
                    key={f.id}
                    colSpan={2}
                    className="text-center border-l"
                    style={{ color: "#fff", fontWeight: 600, width: "160px" }}
                  >
                    {f.name}
                  </TableHead>
                ))}
                <TableHead
                  colSpan={2}
                  className="text-center border-l"
                  style={{ color: "#fff", fontWeight: 600, backgroundColor: "rgb(0, 56, 130)", width: "160px" }}
                >
                  Total
                </TableHead>
              </TableRow>
              {/* Row 2: BRL / USD sub-labels */}
              <TableRow style={{ backgroundColor: "rgba(212, 163, 123, 0.7)" }} className="hover:opacity-90">
                {fundList.map((f) => (
                  <React.Fragment key={f.id}>
                    <TableHead className="text-center border-l" style={{ color: "#fff", fontWeight: 500, width: "80px" }}>
                      BRL
                    </TableHead>
                    <TableHead className="text-center" style={{ color: "#fff", fontWeight: 500, width: "80px" }}>
                      USD
                    </TableHead>
                  </React.Fragment>
                ))}
                <TableHead className="text-center border-l" style={{ color: "#fff", fontWeight: 500, backgroundColor: "rgb(0, 56, 130)", width: "80px" }}>
                  BRL
                </TableHead>
                <TableHead className="text-center" style={{ color: "#fff", fontWeight: 500, backgroundColor: "rgb(0, 56, 130)", width: "80px" }}>
                  USD
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {grouped.map(({ origin, clients: grpClients }) => (
                <React.Fragment key={origin}>
                  {/* Group header */}
                  <TableRow className="bg-muted/20 hover:bg-muted/20">
                    <TableCell
                      colSpan={1 + fundList.length * 2 + 2}
                      className="text-xs font-semibold text-muted-foreground uppercase tracking-wide py-1.5"
                    >
                      {origin}
                    </TableCell>
                  </TableRow>
                  {grpClients.map((c) => {
                    const totalBrl = fundList.reduce(
                      (s, f) => s + cellValue(c.id, f.id, "brl"),
                      0
                    );
                    const totalUsd = fundList.reduce(
                      (s, f) => s + cellValue(c.id, f.id, "usd"),
                      0
                    );
                    return (
                    <TableRow key={c.id}>
                      <TableCell className="font-medium uppercase border-r" style={{ fontSize: "11px", width: "170px", maxWidth: "170px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.name}</TableCell>
                      {fundList.map((f) => {
                        const isBrl =
                          editing?.clientId === c.id &&
                          editing.fundId === f.id &&
                          editing.field === "brl";
                        const isUsd =
                          editing?.clientId === c.id &&
                          editing.fundId === f.id &&
                          editing.field === "usd";
                        return (
                          <React.Fragment key={f.id}>
                            <TableCell
                              className={`text-center border-l ${isAdmin ? "cursor-pointer" : ""}`}
                              style={{ fontSize: "11px", whiteSpace: "nowrap", padding: "6px 4px" }}
                              onClick={() => isAdmin && !isBrl && startEdit(c.id, f.id, "brl")}
                            >
                              {isBrl ? (
                                <Input
                                  autoFocus
                                  value={editValue}
                                  onChange={(e) => setEditValue(e.target.value)}
                                  onBlur={commit}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") commit();
                                    if (e.key === "Escape") setEditing(null);
                                  }}
                                  className="h-7 text-right w-32"
                                />
                              ) : (
                                <span className={isAdmin ? "hover:underline decoration-dotted" : ""}>
                                  {cellValue(c.id, f.id, "brl")
                                    ? fmtBRL0(cellValue(c.id, f.id, "brl"))
                                    : "—"}
                                </span>
                              )}
                            </TableCell>
                            <TableCell
                              className={`text-center ${isAdmin ? "cursor-pointer" : ""}`}
                              style={{ fontSize: "11px", whiteSpace: "nowrap", padding: "6px 4px" }}
                              onClick={() => isAdmin && !isUsd && startEdit(c.id, f.id, "usd")}
                            >
                              {isUsd ? (
                                <Input
                                  autoFocus
                                  value={editValue}
                                  onChange={(e) => setEditValue(e.target.value)}
                                  onBlur={commit}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") commit();
                                    if (e.key === "Escape") setEditing(null);
                                  }}
                                  className="h-7 text-right w-32"
                                />
                              ) : (
                                <span className={isAdmin ? "hover:underline decoration-dotted" : ""}>
                                  {cellValue(c.id, f.id, "usd")
                                    ? fmtUSD0(cellValue(c.id, f.id, "usd"))
                                    : "—"}
                                </span>
                              )}
                            </TableCell>
                          </React.Fragment>
                        );
                      })}
                      <TableCell className="text-center border-l font-semibold" style={{ fontSize: "11px", whiteSpace: "nowrap", padding: "6px 4px", backgroundColor: "rgb(0, 56, 130)", color: "#e2eaf5" }}>
                        {totalBrl ? fmtBRL0(totalBrl) : "—"}
                      </TableCell>
                      <TableCell className="text-center font-semibold" style={{ fontSize: "11px", whiteSpace: "nowrap", padding: "6px 4px", backgroundColor: "rgb(0, 56, 130)", color: "#e2eaf5" }}>
                        {totalUsd ? fmtUSD0(totalUsd) : "—"}
                      </TableCell>
                    </TableRow>
                    );
                  })}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
